import React, { useState } from 'react';
import moment from 'moment';
import SlaOverrideReasonFields, { useSlaOverrideReason } from './sla-override-reason-fields';
import { SLA_OVERRIDE_REASON_OPTIONS } from './sla-override-reason-options';

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
    // Sticky header lives in the same scroll container as the body so the two
    // share one <colgroup> and stay column-aligned. The inset box-shadow keeps
    // the divider line visible (collapsed borders drop out when sticky in Chrome).
    position: 'sticky',
    top: 0,
    zIndex: 1,
    boxShadow: 'inset 0 -1px 0 #e5e7eb',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: '13px',
    color: '#1f2937',
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
    tableLayout: 'fixed',
};

/** Flight: Flight, Aircraft, Actual On Blocks, Required, First Resource, Pax Collected, Time Diff, Tasks, Pax, Pass, Reason, Action */
const columnWidthsFlight = ['7%', '8%', '8%', '9%', '10%', '9%', '6%', '5%', '5%', '6%', '9%', '5%'];
/** Passenger: Passenger Name, Passenger ID, Assistance, Rejection Reason, Flight, Aircraft, Actual On Blocks, Required, First Resource, Pax Collected, Time Diff, Tasks, Pass, Reason, Action */
const columnWidthsPassenger = ['8%', '8%', '6%', '6%', '6%', '7%', '7%', '7%', '7%', '7%', '5%', '5%', '5%', '7%', '5%'];

const modalOverlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
};

const modalContentStyle = {
    backgroundColor: '#fff',
    borderRadius: '8px',
    padding: '24px',
    minWidth: '320px',
    maxWidth: '90vw',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
};

const InboundOverrideModal = ({ overrideContext, open, onClose, onApply, isPatching, sameFlightCount = 1 }) => {
    const [selectedPass, setSelectedPass] = React.useState(true);
    const [applyToAll, setApplyToAll] = React.useState(false);
    const { selectedCode, setSelectedCode, otherText, setOtherText, resolved } = useSlaOverrideReason(
        open,
        overrideContext?.row?.bookingId,
    );

    React.useEffect(() => {
        if (open && overrideContext) {
            setSelectedPass(true);
            setApplyToAll(false);
        }
    }, [open, overrideContext]);

    if (!open) return null;

    const canApply = !!resolved;
    const flightLabel = overrideContext?.row?.flightNumber || 'this flight';

    const handleApply = () => {
        if (!overrideContext || !onApply || !resolved) return;
        onApply(overrideContext.row, selectedPass, resolved, { applyToAll: applyToAll && sameFlightCount > 1 });
    };

    return (
        <div style={modalOverlayStyle} onClick={isPatching ? undefined : onClose} role="dialog" aria-modal="true" aria-labelledby="inbound-override-title">
            <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
                <h2 id="inbound-override-title" style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600, color: '#1f2937' }}>
                    Inbound SLA Override
                </h2>
                <p style={{ margin: '0 0 16px', fontSize: '14px', color: '#6b7280' }}>
                    Manually set pass or fail for {overrideContext?.row?.passengerName || overrideContext?.row?.flightNumber || 'this passenger'}
                    {overrideContext?.readyMinutes ? ` (${overrideContext.readyMinutes} min KPI)` : ''}.
                </p>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="override" checked={selectedPass} onChange={() => setSelectedPass(true)} />
                        Auto Pass (Manual Pass)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                        <input type="radio" name="override" checked={!selectedPass} onChange={() => setSelectedPass(false)} />
                        Auto Fail (Manual Fail)
                    </label>
                    <SlaOverrideReasonFields
                        options={SLA_OVERRIDE_REASON_OPTIONS.inbounds}
                        selectedCode={selectedCode}
                        onSelectedCodeChange={setSelectedCode}
                        otherText={otherText}
                        onOtherTextChange={setOtherText}
                    />
                    {sameFlightCount > 1 && (
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '16px', cursor: 'pointer', fontSize: '14px', color: '#374151' }}>
                            <input
                                type="checkbox"
                                checked={applyToAll}
                                onChange={(e) => setApplyToAll(e.target.checked)}
                                style={{ marginTop: '2px', width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6' }}
                            />
                            Apply to all {sameFlightCount} passengers on {flightLabel}
                        </label>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', alignItems: 'center' }}>
                    {isPatching && (
                        <span style={{ marginRight: '8px', fontSize: '13px', color: '#6b7280' }}>Updating...</span>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isPatching}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#f3f4f6',
                            color: '#374151',
                            border: '1px solid #e5e7eb',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.6 : 1,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleApply}
                        disabled={isPatching || !canApply}
                        style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            cursor: isPatching ? 'not-allowed' : 'pointer',
                            backgroundColor: '#3b82f6',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            opacity: isPatching ? 0.8 : 1,
                        }}
                    >
                        {isPatching ? 'Updating...' : 'Apply'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const groupByFlight = (rawData) => {
    const map = {};
    (rawData || []).forEach((row) => {
        const fid = row.flightId || 'unknown';
        if (!map[fid]) map[fid] = { ...row, passengerCount: 0 };
        map[fid].passengerCount += 1;
    });
    return Object.values(map);
};

const rowsOnSameFlight = (rawData, row) => {
    const seen = new Set();
    return (rawData || []).filter((candidate) => {
        if (!candidate?.bookingId || seen.has(candidate.bookingId)) return false;
        const sameFlight = row?.flightId
            ? candidate.flightId === row.flightId
            : !!(row?.flightNumber && candidate.flightNumber === row.flightNumber);
        if (!sameFlight) return false;
        seen.add(candidate.bookingId);
        return true;
    });
};

const isNoDataRow = (row) => row.passReason === 'No Ramp Task';

/** Maps report KPI key to per-KPI override key */
const REPORT_KPI_TO_OVERRIDE_KEY = {
  lt230SeatsA: 'lt230_6',
  lt230SeatsB: 'lt230_11',
  lt230SeatsC: 'lt230_21',
  gte230SeatsA: 'gte230_16',
  gte230SeatsB: 'gte230_21',
  gte230SeatsC: 'gte230_31',
};

const ColGroup = ({ widths }) => (
    <colgroup>
        {(widths || columnWidthsFlight).map((width, i) => (
            <col key={i} style={{ width }} />
        ))}
    </colgroup>
);

const subTitleStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    marginTop: '14px',
};

const formatTime = (isoString) => {
    if (!isoString) return '-';
    return moment(isoString).format('HH:mm');
};

const KpiRawDataSection = ({ title, minutes, rawData, date, reportKpiKey, onOpenOverrideModal, breakdownView = 'passenger', includeNoData = false }) => {
    const [isOpen, setIsOpen] = useState(false);

    const filteredRawData = includeNoData ? (rawData || []) : (rawData || []).filter((row) => !isNoDataRow(row));
    const displayData = breakdownView === 'flight' ? groupByFlight(filteredRawData) : filteredRawData;
    const columnWidths = breakdownView === 'flight' ? columnWidthsFlight : columnWidthsPassenger;

    if (!displayData?.length) {
        return (
            <div>
                <p style={subTitleStyle}>{title} ({minutes} min) {date ? `(${date})` : ''}</p>
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>No records.</p>
            </div>
        );
    }

    const containerStyle = {
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
        padding: '12px',
        marginBottom: '12px',
        backgroundColor: '#fff',
    };

    return (
        <div style={containerStyle}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <span style={{
                    display: 'inline-block',
                    fontSize: '12px',
                    color: '#9ca3af',
                    transition: 'transform 0.2s ease',
                    transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                }}>
                    &#9654;
                </span>
                <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>{title} ({minutes} min) ({displayData.length}) {date ? `(${date})` : ''}</p>
            </div>

            {!isOpen ? null : (
            <div style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '10px' }}>
                <table style={tableStyle}>
                    <ColGroup widths={columnWidths} />
                    <thead>
                        <tr>
                            {breakdownView === 'passenger' && (
                                <>
                                    <th style={headerStyle}>Passenger Name</th>
                                    <th style={headerStyle}>Passenger ID</th>
                                    <th style={headerStyle}>Assistance</th>
                                    <th style={headerStyle}>Rejection Reason</th>
                                </>
                            )}
                            <th style={headerStyle}>Flight</th>
                            <th style={headerStyle}>Aircraft Info</th>
                            <th style={headerStyle}>Actual On Blocks</th>
                            <th style={headerStyle}>Required At Location</th>
                            <th style={headerStyle}>First Resource At Location</th>
                            <th style={headerStyle}>Pax Collected</th>
                            <th style={{ ...headerStyle, textAlign: 'center' }}>Time Diff (min)</th>
                            <th style={{ ...headerStyle, textAlign: 'center' }}>Tasks</th>
                            {breakdownView === 'flight' && <th style={{ ...headerStyle, textAlign: 'center' }}>Pax</th>}
                            <th style={{ ...headerStyle, textAlign: 'center' }}>Pass</th>
                            <th style={headerStyle}>Reason</th>
                            <th style={{ ...headerStyle, textAlign: 'center' }}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayData.map((row, idx) => (
                            <tr key={row.bookingId || row.flightId + '-' + idx}>
                                {breakdownView === 'passenger' && (
                                    <>
                                        <td style={cellStyle}>{row.passengerName ?? '–'}</td>
                                        <td style={{ ...cellStyle, fontSize: '11px', wordBreak: 'break-all' }}>{row.passengerId ?? '–'}</td>
                                        <td style={cellStyle}>{row.assistance ?? '–'}</td>
                                        <td style={cellStyle}>{row.paxRejectionReason ?? '–'}</td>
                                    </>
                                )}
                                <td style={cellStyle}>{row.flightNumber}</td>
                                <td style={cellStyle}>{row.aircraftInfo ?? '-'}</td>
                                <td style={cellStyle}>{formatTime(row.actualOnBlocks)}</td>
                                <td style={cellStyle}>{formatTime(row.requiredAtLocation)}</td>
                                <td style={cellStyle}>{formatTime(row.firstResourceAtLocation)}</td>
                                <td style={cellStyle}>{formatTime(row.rampPaxCollectTime)}</td>
                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                    {row.timeDifferenceMinutes != null ? row.timeDifferenceMinutes.toFixed(1) : '-'}
                                </td>
                                <td style={{ ...cellStyle, textAlign: 'center' }}>{row.taskCount ?? '-'}</td>
                                {breakdownView === 'flight' && (
                                    <td style={{ ...cellStyle, textAlign: 'center' }}>{row.passengerCount ?? 1}</td>
                                )}
                                <td style={{
                                    ...cellStyle,
                                    textAlign: 'center',
                                    backgroundColor: row.passReason === 'No Ramp Task' ? '#dbeafe' : row.pass ? '#dcfce7' : '#fee2e2',
                                    color: row.passReason === 'No Ramp Task' ? '#1d4ed8' : row.pass ? '#166534' : '#991b1b',
                                }}>
                                    {row.passReason === 'No Ramp Task' ? 'Skip' : row.pass ? (row.isManualOverride ? 'Pass (Manual)' : 'Pass') : (row.isManualOverride ? 'Fail (Manual)' : 'Fail')}
                                </td>
                                <td style={cellStyle}>{row.passReason || '-'}</td>
                                <td style={{ ...cellStyle, textAlign: 'center' }}>
                                    {onOpenOverrideModal && row.bookingId && breakdownView === 'passenger' ? (
                                        <button
                                            type="button"
                                            onClick={() => onOpenOverrideModal(row, date, REPORT_KPI_TO_OVERRIDE_KEY[reportKpiKey] || reportKpiKey, minutes, reportKpiKey)}
                                            style={{
                                                padding: '6px 12px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                backgroundColor: '#3b82f6',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '6px',
                                            }}
                                        >
                                            Action
                                        </button>
                                    ) : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}
        </div>
    );
};

const InboundSlaRawDataTable = ({ dailyReports, overrideContext, onOverrideRowChange, onPatchInbound, isPatching, includeNoData = false }) => {
    const [breakdownView, setBreakdownView] = useState('passenger');

    if (!dailyReports?.length) return null;

    const handleOpenOverrideModal = (row, operationDate, kpiKey, readyMinutes, reportKpiKey) => {
        onOverrideRowChange?.({ row, operationDate, kpiKey, readyMinutes, reportKpiKey });
    };

    const sameFlightRows = (() => {
        if (!overrideContext?.row) return [];
        const report = (dailyReports || []).find((r) => r.date === overrideContext.operationDate);
        const rawData = report?.kpis?.[overrideContext.reportKpiKey]?.rawData || [];
        const matches = rowsOnSameFlight(rawData, overrideContext.row)
            .filter((candidate) => includeNoData || !isNoDataRow(candidate));
        return matches.length ? matches : (overrideContext.row.bookingId ? [overrideContext.row] : []);
    })();

    return (
        <div style={{ marginTop: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>Breakdown view:</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                        type="radio"
                        name="breakdownView"
                        checked={breakdownView === 'passenger'}
                        onChange={() => setBreakdownView('passenger')}
                    />
                    Passenger (name + assistance)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                    <input
                        type="radio"
                        name="breakdownView"
                        checked={breakdownView === 'flight'}
                        onChange={() => setBreakdownView('flight')}
                    />
                    Flight
                </label>
            </div>
            {dailyReports.map((report) => {
                const kpiKeys = report.kpis ? Object.keys(report.kpis) : [];

                return (
                    <div key={report.date} style={{ marginBottom: '24px' }}>
                        {kpiKeys.map((key) => {
                            const kpi = report.kpis[key];
                            return (
                                <KpiRawDataSection
                                    key={key}
                                    reportKpiKey={key}
                                    title={kpi.title}
                                    minutes={kpi.minutes}
                                    rawData={kpi.rawData}
                                    date={report.date}
                                    onOpenOverrideModal={handleOpenOverrideModal}
                                    breakdownView={breakdownView}
                                    includeNoData={includeNoData}
                                />
                            );
                        })}
                    </div>
                );
            })}
            <InboundOverrideModal
                overrideContext={overrideContext}
                open={!!overrideContext}
                onClose={() => onOverrideRowChange?.(null)}
                onApply={(row, pass, reason, { applyToAll } = {}) => {
                    onPatchInbound?.(row, pass, reason, { rows: applyToAll ? sameFlightRows : undefined });
                }}
                isPatching={isPatching}
                sameFlightCount={sameFlightRows.length}
            />
        </div>
    );
};

export default InboundSlaRawDataTable;
